import {injectable} from 'inversify';
import {computed, makeAutoObservable, observable} from 'mobx';

import {BaseModel} from '../../domain/Base/Base.model';
import {createWeakRef, isWeakRefSupported} from './weakRef.polyfill';

type ModelID = BaseModel['id'];

@injectable()
export class ModelsRegistry {
  private readonly registry = new Map<ModelID, WeakRef<BaseModel>>();
  private readonly registryByClass = new Map<
    string,
    Map<ModelID, WeakRef<BaseModel>>
  >();

  constructor() {
    if (!isWeakRefSupported()) {
      // eslint-disable-next-line no-console
      console.warn(
        '[ModelsRegistry] WeakRef is not supported in this environment. Using polyfill instead.',
      );
    }

    makeAutoObservable<ModelsRegistry, 'registry' | 'registryByClass'>(
      this,
      {
        registry: observable,
        registryByClass: observable,
      },
      {autoBind: true},
    );
  }

  clear(): void {
    this.registry.clear();
  }

  delete(id: ModelID): void {
    const model = this.get(id);

    if (model) {
      this.registryByClass.get(this.getModelName(model))?.delete(id);
    }

    this.registry.delete(id);
  }

  deleteMany(predicate: (model: BaseModel) => boolean): void {
    const toDelete: {
      id: ModelID;
      name: string;
    }[] = [];

    for (const ref of this.registry.values()) {
      const model = ref.deref();

      if (model && predicate(model)) {
        toDelete.push({id: model.id, name: this.getModelName(model)});
      }
    }

    toDelete.forEach(({id, name}) => {
      this.registry.delete(id);
      this.registryByClass.get(name)?.delete(id);
    });
  }

  find<T extends BaseModel>(predicate: (model: T) => boolean): T | undefined {
    return computed(() => {
      for (const ref of this.registry.values()) {
        const model = ref.deref() as T | undefined;

        if (model && predicate(model)) {
          return model;
        }
      }

      return undefined;
    }).get();
  }

  findMany<T extends BaseModel>(predicate: (model: T) => boolean): T[] {
    return computed(() => {
      const result: T[] = [];

      for (const ref of this.registry.values()) {
        const model = ref.deref() as T | undefined;

        if (model && predicate(model)) {
          result.push(model);
        }
      }

      return result;
    }).get();
  }

  get<T extends BaseModel>(id: ModelID) {
    return computed(() => {
      const ref = this.registry.get(id);

      if (!ref) return undefined;

      const model = ref.deref() as T | undefined;

      if (!model) {
        this.registry.delete(id);
      }

      return model;
    }).get();
  }

  getByClass<T extends typeof BaseModel>(id: ModelID, modelClass: T) {
    const name = modelClass.modelName;

    return computed(() => {
      return this.registryByClass.get(name)?.get(id)?.deref() as
        | InstanceType<T>
        | undefined;
    }).get();
  }

  getManyByClass<T extends typeof BaseModel>(modelClass: T) {
    const name = modelClass.modelName;

    return computed(() => {
      const result: InstanceType<T>[] = [];

      for (const ref of this.registryByClass.get(name)?.values() ?? []) {
        const model = ref.deref() as InstanceType<T> | undefined;

        if (model) {
          result.push(model);
        }
      }

      return result;
    }).get();
  }

  put<T extends BaseModel>(model: T): T {
    const modelName = this.getModelName(model);

    this.registry.set(model.id, createWeakRef(model));

    if (!this.registryByClass.has(modelName)) {
      this.registryByClass.set(
        modelName,
        new Map<ModelID, WeakRef<BaseModel>>(),
      );
    }

    this.registryByClass.get(modelName)!.set(model.id, createWeakRef(model));

    return this.get<T>(model.id) as T;
  }

  private getModelName(model: BaseModel): string {
    return model.modelName;
  }
}
